<script setup lang="ts">
import { ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useAuthStore } from '@/stores/auth.js';
import ProfileDialog from '@/components/ProfileDialog.vue';
import ThemeSwitch from '@/components/ThemeSwitch.vue';
import {
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuPortal,
  DropdownMenuContent,
  DropdownMenuItem,
} from 'radix-vue';

const auth = useAuthStore();
const { user, org, loading, isStaff } = storeToRefs(auth);

// The profile dialog's open state lives out here, NOT inside the menu: the
// portaled menu content is unmounted the moment the menu closes, so a dialog
// nested in it would vanish the instant it was chosen. Radix fires an item's
// `select` before that unmount, so flipping this ref from the item is safe.
const profileOpen = ref(false);

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
</script>

<template>
  <div class="app-shell">
    <header class="app-shell__header">
      <div class="app-shell__bar">
        <div class="app-shell__left">
          <RouterLink to="/projects" class="app-shell__brand">Hubl CI </RouterLink>
          <RouterLink v-if="isStaff" to="/admin/orgs" class="app-shell__nav-link">
            Admin
          </RouterLink>
        </div>

        <div v-if="loading" class="app-shell__user-loading">
          <div class="app-shell__skeleton app-shell__skeleton--text" />
          <div class="app-shell__skeleton app-shell__skeleton--avatar" />
        </div>

        <div v-else-if="user" class="app-shell__user">
          <ThemeSwitch />
          <span v-if="org" class="app-shell__org">{{ org.name }}</span>
          <DropdownMenuRoot>
            <DropdownMenuTrigger
              class="app-shell__avatar"
              :aria-label="`Signed in as ${user.displayName || user.email}`"
            >
              {{ initials(user.displayName || user.email) }}
            </DropdownMenuTrigger>
            <DropdownMenuPortal>
              <DropdownMenuContent class="app-shell__menu" :side-offset="4" align="end">
                <div class="app-shell__menu-identity">
                  <div class="app-shell__menu-name">
                    {{ user.displayName || user.email }}
                  </div>
                  <div class="app-shell__menu-email">{{ user.email }}</div>
                </div>
                <div class="app-shell__menu-divider" />
                <DropdownMenuItem
                  as="button"
                  type="button"
                  class="app-shell__menu-item"
                  @select="profileOpen = true"
                >
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem as-child>
                  <RouterLink to="/logout" class="app-shell__menu-item">
                    Log out
                  </RouterLink>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenuPortal>
          </DropdownMenuRoot>
          <ProfileDialog v-model:open="profileOpen" />
        </div>
      </div>
    </header>
    <div class="app-shell__main">
      <slot />
    </div>
  </div>
</template>

<style scoped>
@keyframes app-shell-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.app-shell {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--color-surface-page);
}

.app-shell__header {
  background: var(--color-surface-raised);
  border-bottom: 1px solid var(--border-subtle);
}

.app-shell__bar {
  max-width: 80rem;
  height: 3.5rem;
  margin: 0 auto;
  padding: 0 var(--space-4);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.app-shell__left {
  display: flex;
  align-items: center;
  gap: var(--space-4);
}

.app-shell__brand {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  text-decoration: none;
}

.app-shell__nav-link {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
  text-decoration: none;
  border-radius: var(--radius-sm);
}

.app-shell__nav-link:hover {
  color: var(--color-text-primary);
}

.app-shell__nav-link.router-link-active {
  color: var(--color-text-brand);
}

.app-shell__nav-link:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--border-focus);
}

.app-shell__user-loading,
.app-shell__user {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.app-shell__skeleton {
  background: var(--color-surface-skeleton);
  border-radius: var(--radius-sm);
  animation: app-shell-pulse 1.5s ease-in-out infinite;
}

.app-shell__skeleton--text {
  height: 1rem;
  width: 5rem;
}

.app-shell__skeleton--avatar {
  height: 2rem;
  width: 2rem;
  border-radius: var(--radius-full);
}

.app-shell__org {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

@media (max-width: 639px) {
  .app-shell__org {
    display: none;
  }
}

.app-shell__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 2rem;
  width: 2rem;
  border: 0;
  border-radius: var(--radius-full);
  background: var(--color-surface-brand-subtle);
  color: var(--color-text-brand);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
}

.app-shell__avatar:hover {
  background: var(--color-surface-brand-subtle-hover);
}

.app-shell__avatar:focus {
  outline: none;
  box-shadow: 0 0 0 2px var(--border-focus);
}

.app-shell__main {
  flex: 1;
}
</style>

<!--
  The account menu's content is portaled to <body> via DropdownMenuPortal. Vue's
  scoped-CSS `data-v` hash does NOT reach that portaled subtree — Radix's
  multi-layer Content wrapper drops the scope attribute — so these rules matched
  nothing while they lived in the scoped block above and the menu rendered as
  bare floating text over the page. They must be global to reach the portaled
  node (the CommentActionMenu precedent). The class names are component-specific
  BEM, so the global scope is safe. Trigger-side rules stay scoped: the avatar
  lives in this component's own subtree.
-->
<style>
.app-shell__menu {
  min-width: 10rem;
  padding: var(--space-1);
  background: var(--color-surface-raised);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
}

.app-shell__menu-identity {
  padding: 0.375rem var(--space-2);
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
}

.app-shell__menu-name {
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
}

.app-shell__menu-email {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.app-shell__menu-divider {
  height: 1px;
  margin: var(--space-1) 0;
  background: var(--border-faint);
}

/* Two flavours share this rule: the RouterLink "Log out" and the <button>
   "Profile" (an action, not navigation). The font/background/border/text-align
   declarations are the button's UA-chrome reset — inert for the anchor — so both
   items render identically. */
.app-shell__menu-item {
  display: block;
  width: 100%;
  padding: 0.375rem var(--space-2);
  font: inherit;
  font-size: var(--font-size-sm);
  text-align: left;
  color: var(--color-text-strong);
  text-decoration: none;
  background: none;
  border: 0;
  border-radius: var(--radius-sm);
  cursor: default;
}

.app-shell__menu-item:hover {
  background: var(--color-interactive-hover-strong);
}

/* Radix moves focus item-to-item on arrow keys, so this is the keyboard path's
   only affordance. Same ring as the header's nav links. */
.app-shell__menu-item:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--border-focus);
}
</style>
